/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView, ActionButton, Text, TextField } from '@components/atoms'
// NOTE: OrderViewTab(세로 탭) 임시 숨김 처리 — 테이블 뷰만 사용
// import { OrderViewTab } from '@components/systems'
import { Grid, orderColumns } from '@grid'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
	Select,
	SelectChangeEvent,
	MenuItem,
	FormControl,
	InputLabel,
} from '@mui/material'

import { GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid'
import {
	useAppSelector,
	useAppDispatch,
	orderActions,
	routeActions,
	ApiGetDataStatus,
} from '@redux/index'
import {
	OrderDetailModal,
	ORDER_STATUS_TITLE,
	fulfillmentChipTitle,
} from './OrderDetailModal'
import { ShipmentMessageModal } from '@components/systems/shipping/ShipmentModals'
import { WholesaleOrderCreateModal } from './WholesaleOrderCreateModal'
import { configActions } from '@redux/store/index'
import { OrderType, SeasonType } from '@schemas'
import dayjs from 'dayjs'
import {
	mono,
	boShared,
	boStatusChipClass,
} from '@components/systems/backoffice/backoffice.tokens'

export const OrderGrid = () => {
	const reduxDispatch = useAppDispatch()

	// redux state
	const orderList: OrderType[] = useAppSelector(state => state.order.orderList)
	const orderStatus: ApiGetDataStatus = useAppSelector(state => state.order.orderStatus)
	const selectedOrderIds: string[] = useAppSelector(state => state.order.selectedOrderIds)
	const selectedStatusArr: string[] = useAppSelector(
		state => state.order.selectedStatusArr,
	)
	const searchStartDate: string = useAppSelector(state => state.order.searchStartDate)
	const searchEndDate: string = useAppSelector(state => state.order.searchEndDate)
	const searchStatus: string = useAppSelector(state => state.order.searchStatus)
	const searchChannel: string = useAppSelector(state => state.order.searchChannel)
	const searchKeyword: string = useAppSelector(state => state.order.searchKeyword)
	const searchMode: 'date' | 'season' = useAppSelector(state => state.order.searchMode)
	const searchSeasonId: string = useAppSelector(state => state.order.searchSeasonId)
	const seasonOptions: SeasonType[] = useAppSelector(state => state.order.seasonOptions)

	const hasSelection = selectedOrderIds.length >= 1

	/*
		버튼은 **주문확인 하나**다. 라벨 생성 · 라벨 인쇄 · 배송 업데이트는 배송 관리로 옮겼다
		(2026-09-22, SHIPPING_PLAN §2.2.1) — 주문확인을 하면 그 주문이 배송 관리 목록에 선다.
		흐름이 오더 → 배송 한 방향이 된다.
	*/
	// 주문확인 — 신규 · 입금 대기(도매, 입금 전 출고). 입금은 뒤에 정산에서 처리해도 된다 (2026-09-27 사용자 결정)
	const showOrderPreparing =
		hasSelection && selectedStatusArr.every(s => s === 'new' || s === 'awaitingPayment')
	// 입금 확인 — 선택이 전부 도매 · 미입금일 때(배송 상태 무관). 월말 정산 입금을 몰아서 처리한다 (DISPATCH_PLAN §5.2)
	const selectedOrders = React.useMemo(
		() => orderList.filter(o => selectedOrderIds.includes(o.id)),
		[orderList, selectedOrderIds],
	)
	const showMarkPaid =
		hasSelection &&
		selectedOrders.length === selectedOrderIds.length &&
		selectedOrders.every(
			o => o.channel === 'wholesale' && o.paidYn === 0 && o.status !== 'deleted',
		)
	/** 도매 주문 대리 입력 모달 */
	const [isCreateOpen, setIsCreateOpen] = React.useState<boolean>(false)

	const onRowSelectionModelChange = (newRowSelectionModel: GridRowSelectionModel) => {
		reduxDispatch(orderActions.SELECTED_ORDER_IDS(newRowSelectionModel as string[]))
	}

	const onStartDateChange = (date: any) => {
		const newDate = dayjs(date).format('YYYY-MM-DD')
		reduxDispatch(orderActions.SEARCH_START_DATE(newDate))
	}

	const onEndDateChange = (date: any) => {
		const newDate = dayjs(date).format('YYYY-MM-DD')
		reduxDispatch(orderActions.SEARCH_END_DATE(newDate))
	}

	const onStatusChange = (e: SelectChangeEvent) => {
		const newStatus = e.target.value
		reduxDispatch(orderActions.SEARCH_STATUS(newStatus))
	}

	const onKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value
		reduxDispatch(orderActions.SEARCH_KEYWORD(str))
	}

	const onOrderPreparingClick = () => {
		reduxDispatch(orderActions.TRIGGER_ORDER_PREPARING(true))
	}

	const onMarkPaidClick = () => {
		reduxDispatch(orderActions.TRIGGER_ORDER_MARK_PAID(true))
	}

	const onWholesaleCreated = (orderId: string) => {
		reduxDispatch(orderActions.TRIGGER_ORDER_SEARCH(true))
		reduxDispatch(
			configActions.TOAST({
				isShow: true,
				message: `도매 주문 ${orderId} — 입금 대기로 들어왔습니다.`,
			}),
		)
	}

	return (
		<View css={style}>
			{/* OrderViewTab(세로 탭) 임시 숨김 처리 — 테이블 뷰만 사용 */}
			<BoxView
				title="오더 관리"
				className="orderGirdBoxView"
				leftHeaderAccessory={
					<View className="orderGridLeftBoxTopView">
						<button
							type="button"
							className="boBtn orderSubmitButton"
							onClick={() => {
								reduxDispatch(orderActions.TRIGGER_ORDER_SEARCH(true))
							}}
						>
							검색
						</button>
						{/* 검색 기준 — 기간(생성일) | 시즌. 시즌이면 날짜 대신 시즌 셀렉트 */}
						<FormControl>
							<InputLabel id="order-search-mode-label">기준</InputLabel>
							<Select
								className="modeSelect"
								labelId="order-search-mode-label"
								label="기준"
								value={searchMode}
								size="small"
								onChange={(e: SelectChangeEvent) =>
									reduxDispatch(
										orderActions.SEARCH_MODE(e.target.value as 'date' | 'season'),
									)
								}
							>
								<MenuItem value="date">기간</MenuItem>
								<MenuItem value="season">시즌</MenuItem>
							</Select>
						</FormControl>

						{searchMode === 'season' ? (
							<FormControl>
								<InputLabel id="order-season-select-label" shrink>
									시즌
								</InputLabel>
								<Select
									className="seasonSelect"
									labelId="order-season-select-label"
									label="시즌"
									value={searchSeasonId}
									size="small"
									displayEmpty
									onChange={(e: SelectChangeEvent) =>
										reduxDispatch(orderActions.SEARCH_SEASON_ID(e.target.value))
									}
								>
									<MenuItem value="">전체 시즌</MenuItem>
									{seasonOptions.map(s => (
										<MenuItem key={s.id} value={s.id}>
											{s.title}
											<span style={{ marginLeft: 8, fontSize: 11, color: mono.faint }}>
												{s.startDate.slice(0, 10)}
											</span>
										</MenuItem>
									))}
								</Select>
							</FormControl>
						) : (
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<View className="datePickerView">
									<DatePicker
										className="datePicker"
										label="시작일자"
										value={new Date(searchStartDate)}
										format="yyyy-MM-dd"
										onChange={onStartDateChange}
										slotProps={{
											textField: {
												variant: 'outlined',
												size: 'small',
												style: {
													width: '8rem',
												},
												placeholder: 'YYYY-MM-DD',
											},
										}}
									/>
									<Text className="datePickerText">~</Text>
									<DatePicker
										className="datePicker"
										label="종료일자"
										format="yyyy-MM-dd"
										value={new Date(searchEndDate)}
										onChange={onEndDateChange}
										slotProps={{
											textField: {
												variant: 'outlined',
												size: 'small',
												style: {
													width: '8rem',
												},
												placeholder: 'YYYY-MM-DD',
											},
										}}
									/>
								</View>
							</LocalizationProvider>
						)}

						<View className="orderGridSearchView">
							{/* 채널 — 소매(쇼핑몰) | 도매(거래처). 값은 기억한다 (DISPATCH_PLAN §5) */}
							<FormControl className="channelControl">
								<InputLabel id="order-channel-select-label">채널</InputLabel>
								<Select
									className="channelSelect"
									labelId="order-channel-select-label"
									label="채널"
									value={searchChannel}
									size="small"
									onChange={(e: SelectChangeEvent) =>
										reduxDispatch(orderActions.SEARCH_CHANNEL(e.target.value))
									}
								>
									<MenuItem value="all">전체</MenuItem>
									<MenuItem value="retail">소매</MenuItem>
									<MenuItem value="wholesale">도매</MenuItem>
								</Select>
							</FormControl>
							<FormControl fullWidth>
								<InputLabel id="delivery-state-select-label">상태</InputLabel>
								<Select
									className="categSelect"
									name="categ"
									label="상태"
									value={searchStatus}
									size="small"
									onChange={onStatusChange}
								>
									<MenuItem value="all">전체</MenuItem>
									<MenuItem value="awaitingPayment">입금 대기</MenuItem>
									<MenuItem value="new">신규 주문</MenuItem>
									<MenuItem value="preparing">배송 준비중</MenuItem>
									<MenuItem value="inTransit">배송중</MenuItem>
									<MenuItem value="completed">배송완료</MenuItem>
									<MenuItem value="refunded">환불</MenuItem>
									<MenuItem value="deleted">삭제</MenuItem>
								</Select>
							</FormControl>

							<TextField
								type="text"
								label="검색"
								size="small"
								value={searchKeyword}
								onChange={onKeywordChange}
							/>
						</View>
					</View>
				}
				rightHeaderAccessory={
					<View className="orderGridRightBoxTopView">
						{showMarkPaid && (
							<ActionButton
								onClick={onMarkPaidClick}
								title="입금 확인"
								iconName="cash-check"
							/>
						)}
						{showOrderPreparing && (
							<ActionButton
								onClick={onOrderPreparingClick}
								title="주문확인"
								iconName="check-circle-outline"
							/>
						)}
						{/* 거래처가 카톡·전화로 준 주문을 관리자가 대신 넣는다 (DISPATCH_PLAN §5.2) */}
						<ActionButton
							onClick={() => setIsCreateOpen(true)}
							title="도매 주문 추가"
							iconName="plus"
							variant="outlined"
						/>
					</View>
				}
			>
				<Grid
					title="오더 관리"
					rows={orderList}
					columns={orderColumns}
					loading={orderStatus === 'loading'}
					isEditable={false}
					checkboxSelection={true}
					rowSelectionModel={selectedOrderIds}
					onRowSelectionModelChange={onRowSelectionModelChange}
					onDetailClick={row =>
						reduxDispatch(
							routeActions.NAVIGATE_PATH(
								`/main/order/${encodeURIComponent(String(row.id))}`,
							),
						)
					}
					renderCell={{
						status: (params: GridRenderCellParams<OrderType, any, any>) => {
							if (params.value === null) {
								return <></>
							}

							const statusTitle = ORDER_STATUS_TITLE[params.value] ?? params.value

							return (
								<>
									<span className={boStatusChipClass(params.value)}>{statusTitle}</span>
									{fulfillmentChipTitle(params.row) !== '' && (
										<span className="boChip outline">
											{fulfillmentChipTitle(params.row)}
										</span>
									)}
									{params.row.channel === 'wholesale' &&
										params.row.paidYn === 0 &&
										params.value !== 'awaitingPayment' && (
											<span className={boStatusChipClass('awaitingPayment')}>미입금</span>
										)}
								</>
							)
						},
						invoiceUrl: (params: GridRenderCellParams<OrderType, any, any>) => {
							return (
								<a href={params.value} target="_blank" rel="noreferrer">
									인보이스 링크
								</a>
							)
						},
					}}
					renderEditCell={
						{
							// Add any custom edit cell rendering if needed
						}
					}
				/>
			</BoxView>

			<ShipmentMessageModal />
			<OrderDetailModal />
			<WholesaleOrderCreateModal
				open={isCreateOpen}
				onClose={() => setIsCreateOpen(false)}
				onCreated={onWholesaleCreated}
			/>
		</View>
	)
}

const style = css({
	...boShared,
	height: '100%',
	margin: 'auto',
	background: 'transparent',
	borderRadius: 0,

	'.orderGridLeftBoxTopView': {
		display: 'flex',
		alignItems: 'center',
		gap: 8,

		'.orderSubmitButton': {
			marginRight: 4,
		},
		'.modeSelect': { minWidth: '6rem' },
		'.seasonSelect': { minWidth: '14rem' },
		'.datePickerView': {
			display: 'flex',
			alignItems: 'center',

			'.datePicker': {
				width: '9rem',

				'.MuiInputBase-input': {
					fontSize: 12,
					height: '1.5rem',
				},
			},

			'.datePickerText': {
				margin: '0 8px',
				color: mono.faint,
			},
		},

		'.orderGridSearchView': {
			gap: 8,
			display: 'flex',
			alignItems: 'center',
			// 소매 · 도매 — 「상태」와 같은 폭. Select 에만 폭을 주면 FormControl(54px) 밖으로 넘쳐 옆 셀렉트와 겹친다 (2026-09-27)
			'.channelControl': { minWidth: '8rem' },
			'.categSelect': { minWidth: '8rem' },
		},
	},
	'.orderGridRightBoxTopView': {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		width: '100%',
	},
})
